import { Button, Link, Loader, TextInput } from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import type { FC } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useTenant } from "@contexts/Tenant/Tenant.context";
import { FORM_VALIDATION_MODE } from "@core/core.constants";
import { ArrowRightMd, CloseMd } from "@icons";
import { ROUTES } from "@router/router.constants";
import { usePostResetPassword } from "@services/apiEcms/usePostResetPassword";
import { emailRegex } from "@utils/form";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./LoginReset.module.css";
import type { LoginResetForm } from "./LoginReset.types";

export const LoginReset: FC = () => {
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, dirtyFields },
  } = useForm<LoginResetForm>({
    defaultValues: { email: "" },
    mode: FORM_VALIDATION_MODE,
  });
  const { t } = useCustomTranslation(["main", "common"]);
  const { subscriptionUrl } = useTenant();
  const navigate = useNavigate();
  const { mutate, isPending } = usePostResetPassword();
  const onCancel = () => navigate(ROUTES.LOGIN);

  const onSubmit = ({ email }: LoginResetForm) => {
    mutate({ email });
    reset();
  };

  const disabledSubmit = !dirtyFields.email || !!errors.email;

  if (isPending) return <Loader />;

  return (
    <div className={classes.container}>
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <h2 className={classNames("font-title-xs", classes.subtitle)}>
          {t("auth.form.subtitle.resetPassword")}
        </h2>
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => (
            <TextInput
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
              label={t("form.email.label", { ns: "common" })}
              placeholder={t("form.email.placeholder", { ns: "common" })}
              {...field}
            />
          )}
          rules={{
            required: "form.error.required",
            pattern: { value: emailRegex, message: "form.email.hint" },
          }}
        />
        <Button
          className={classes.submitButton}
          data-testid="AuthFormTemplate-login-submit"
          disabled={disabledSubmit}
          endIcon={ArrowRightMd}
          type="submit"
        >
          {t("auth.form.buttons.confirm")}
        </Button>
      </form>
      <Button
        data-testid="AuthFormTemplate-auth-login-cancel"
        onClick={onCancel}
        startIcon={CloseMd}
        variant="tertiary"
      >
        {t("button.cancel", { ns: "common" })}
      </Button>
      {Boolean(subscriptionUrl) && (
        <Link className={classes.link} href={subscriptionUrl} target="_blank">
          {t("auth.form.subscriptionLink")}
        </Link>
      )}
    </div>
  );
};
